import { Box, Container, Link, Paper, Typography } from '@mui/material';
import Footer from './Footer';
import LawAppBar from './LawAppBar';
import './Terms.css';

export default function Terms() {

    return (
        <div className="App">
            <LawAppBar />
            <Container
                maxWidth="lg"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px',
                    textAlign: 'left'
                }}
            >
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
                            marginBottom: '1rem',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2
                        }}
                    >
                        Terms of Service
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: 400,
                            fontStyle: 'italic'
                        }}
                    >
                        Last Updated: February 4, 2026
                    </Typography>
                </Box>

                {/* Introduction */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Welcome to AttorneyAide, a service provided by Veridical Technologies LLC ("Veridical," "AttorneyAide," "we," "us," or "our"). By accessing or using the AttorneyAide platform available at AttorneyAide.com (the "Service"), you ("you," "User," or "Firm") agree to be bound by these Terms of Service ("Terms").
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        If you do not agree to these Terms, you may not use the Service.
                    </Typography>
                </Paper>

                {/* Summary for Managing Partners */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Summary for Managing Partners (Non-Binding)
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px',
                            fontStyle: 'italic'
                        }}
                    >
                        This summary is provided for convenience only and does not replace the full Terms below.
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Ownership: Your firm retains 100% ownership of all uploaded documents and entered information.
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            No AI Training: AttorneyAide does not use Customer Data to train or improve general-purpose AI models.
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            Professional Oversight: You remain the attorney of record and are responsible for reviewing and verifying all outputs using built-in AccuAide™ verification tools.
                        </Typography>
                    </Box>
                </Paper>

                {/* Section 1: Description of the Service */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        1. Description of the Service
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        AttorneyAide is an AI-powered legal technology platform designed to assist personal injury professionals in the United States.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        Not a Law Firm
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Veridical is a technology company and does not provide legal advice, legal opinions, or medical diagnoses.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        No Attorney-Client Relationship
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Use of the Service does not create an attorney-client relationship between Veridical and you, or between Veridical and your clients.
                    </Typography>
                </Paper>

                {/* Section 2: Professional Responsibility and Verification */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        2. Professional Responsibility and Verification
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        AttorneyAide is intended to support—not replace—the professional judgment of licensed attorneys and their authorized staff.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        AccuAide™ Verification Standard
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        The Service employs automated processes that make a best attempt to identify and flag potential data gaps, illegible text, or conflicting information. You acknowledge that AI-assisted outputs may contain inaccuracies, omissions, or incomplete results.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        Duty to Verify
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        You remain solely responsible for reviewing, verifying, and approving all outputs before reliance, filing, or submission. AttorneyAide does not guarantee accuracy, completeness, or fitness for any particular legal outcome.
                    </Typography>
                </Paper>

                {/* Section 3: Intellectual Property Rights */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        3. Intellectual Property Rights
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        3.1. Veridical Intellectual Property
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        As between the parties, Veridical Technologies LLC owns and retains all right, title, and interest in and to the Service, including:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            (a) all software, code, algorithms, and technology used to provide the Service;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            (b) our proprietary AccuAide™ integrity engine and the AttorneyAide™ platform;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            (c) all system-generated metadata and anonymized usage data;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            (d) all documentation, designs, and "look and feel" of the platform; and
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            (e) all intellectual property rights related to the foregoing (collectively, "Veridical IP").
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Except for the limited right to use the Service as expressly granted in these Terms, no license or ownership rights in any Veridical IP are granted to you.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        3.2. Customer Data
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        You retain all right, title, and interest in and to all documents, files, and information you upload or enter into the Service ("User Content"), and all outputs generated by the Service based on such User Content ("Work Product").
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        For purposes of these Terms, User Content and Work Product are collectively referred to as "Customer Data."
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        Veridical acknowledges that Customer Data may constitute an attorney work product and may be protected by the attorney-client privilege or work-product doctrine. Veridical claims no ownership interest in Customer Data.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Veridical acknowledges that the Work Product may be protected by the Attorney-Client Privilege and the Work-Product Doctrine. We claim no ownership interest in your Work Product.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        3.3. License to User Content
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        You grant Veridical a limited, non-exclusive, royalty-free, and worldwide license to host, copy, and process your User Content and Work Product solely for the purpose of providing the Service to you. This license is strictly limited to your private account instance. We do not use your User Content or Work Product to train, retune, or improve our underlying global AI models for use by other customers.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        3.4. Feedback
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        If you provide us with any suggestions, enhancement requests, or other feedback regarding the Service ("Feedback"), you hereby assign to Veridical all right, title, and interest in such Feedback. Veridical shall be free to use such Feedback for any purpose without any obligation or compensation to you.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        3.5. Usage Restrictions
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        You shall not, and shall not permit any third party to:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            (a) reverse engineer, decompile, or attempt to discover the source code or underlying structure of the Veridical IP;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            (b) remove any proprietary notices or branding from the Service or its outputs;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            (c) use the Service to build a competitive product or service; or
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            (d) use any automated system (including "bots" or "scrapers") to extract data from the platform.
                        </Typography>
                    </Box>
                </Paper>

                {/* Section 4: Customer Data, Ownership, and License */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        4. Customer Data, Ownership, and License
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        Ownership
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Customer Data (as defined in Section 3.2) remains the property of the customer.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        Limited License
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        You grant Veridical a limited, non-exclusive, revocable license to host, process, transmit, and analyze Customer Data solely as necessary to provide, maintain, secure, and support the Service, in accordance with these Terms, the Privacy Policy, and any applicable Business Associate Agreement.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        Confidentiality
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Veridical will treat Customer Data as confidential information and will not access, use, or disclose such data except as permitted by these Terms, the Privacy Policy, or as required by law.
                    </Typography>
                </Paper>

                {/* Section 5: Privacy */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        5. Privacy
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Use of the Service is subject to our <a href="/privacy">Privacy Policy</a>.
                    </Typography>
                </Paper>

                {/* Section 6: Acceptable Use */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        6. Acceptable Use
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        You agree not to use the Service:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            For any unlawful purpose or in violation of applicable laws or court orders;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            To upload content you do not have the legal right to use;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            In a manner that violates professional rules of conduct or ethical obligations;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            To interfere with or compromise the security or integrity of the Service.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        You are responsible for all activity conducted under your account.
                    </Typography>
                </Paper>

                {/* Section 7: No Guarantee of Outcomes */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        7. No Guarantee of Outcomes
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        AttorneyAide does not guarantee legal outcomes, settlement values, litigation success, or business results. The Service is a productivity tool and does not replace professional judgment or independent review.
                    </Typography>
                </Paper>

                {/* Section 8: Disclaimer of Warranties */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        8. Disclaimer of Warranties
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        The Service is provided "AS IS" and "AS AVAILABLE." To the maximum extent permitted by law, Veridical disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.
                    </Typography>
                </Paper>

                {/* Section 9: Limitation of Liability */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        9. Limitation of Liability
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        To the maximum extent permitted by law:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Veridical shall not be liable for any indirect, incidental, consequential, special, or punitive damages, including loss of profits, business interruption, or loss of data.
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Veridical's total cumulative liability arising out of or relating to the Service shall not exceed the fees paid by you to Veridical in the twelve months preceding the event giving rise to the claim.
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            Nothing in these Terms limits liability that cannot be limited under applicable law.
                        </Typography>
                    </Box>
                </Paper>

                {/* Section 10: Indemnification */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        10. Indemnification
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        You agree to defend, indemnify, and hold harmless Veridical and its officers, employees, and agents from any claims, damages, liabilities, or expenses arising out of:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Your misuse of the Service;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Your violation of these Terms or applicable law;
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Your breach of professional or ethical obligations; or
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            Your reliance on outputs without appropriate professional verification.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        This obligation does not apply to claims resulting from Veridical's gross negligence or willful misconduct.
                    </Typography>
                </Paper>

                {/* Section 11: Suspension and Termination */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        11. Suspension and Termination
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Veridical may suspend or terminate access to the Service if you materially violate these Terms, pose a security or compliance risk, or if required by law.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Additionally, we may suspend, disable, or delete your Account and/or the Services (or any part of the foregoing) with or without notice, for any or no reason. If the Company deletes your Account, you are prohibited from re-registering for the Services under a different name. In the event of Account deletion for any reason, the Company may, but is not obligated to, delete any of Your Content. The Company shall not be responsible for the failure to delete or deletion of Your Content. All sections which by their nature should survive the termination of these Terms shall continue in full force and effect subsequent to and not withstanding any termination of these Terms by the Company or you. Termination will not limit any of the Company's other rights or remedies at law or in equity.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        You may discontinue use of the Service at any time.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Upon termination, access to Customer Data will be governed by the Privacy Policy and any applicable BAA.
                    </Typography>
                </Paper>

                {/* Section 12: Arbitration; Class Action Waiver */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        12. Arbitration; Class Action Waiver
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        12.1 Mandatory Binding Arbitration
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        Except as expressly provided below, any dispute, claim, or controversy arising out of or relating to these Terms, the Service, or the relationship between you and Veridical (collectively, "Disputes") shall be resolved exclusively through final and binding arbitration, rather than in court.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        The arbitration shall be administered by the American Arbitration Association ("AAA") in accordance with its Commercial Arbitration Rules then in effect. The arbitration shall be conducted by a single neutral arbitrator with experience in commercial technology agreements. The arbitration shall take place in King County, Washington, unless the parties agree otherwise.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        12.2 Exceptions
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Either party may seek injunctive or equitable relief in a court of competent jurisdiction to prevent the unauthorized use, disclosure, or misuse of intellectual property, confidential information, or Customer Data. Such relief shall not be deemed a waiver of the obligation to arbitrate all other Disputes.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        12.3 Class Action Waiver
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px',
                            fontWeight: 500
                        }}
                    >
                        YOU AND VERIDICAL AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, REPRESENTATIVE, OR CONSOLIDATED ACTION OR PROCEEDING.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        The arbitrator shall not have authority to consolidate claims or to hear any class, collective, representative, or private attorney general action.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        12.4 Waiver of Jury Trial
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px',
                            fontWeight: 500
                        }}
                    >
                        TO THE EXTENT ANY DISPUTE IS PERMITTED TO PROCEED IN COURT, YOU AND VERIDICAL HEREBY KNOWINGLY AND IRREVOCABLY WAIVE ANY RIGHT TO A TRIAL BY JURY.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        12.5 Survival
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        This Section shall survive termination of these Terms and your use of the Service.
                    </Typography>
                </Paper>

                {/* Section 13: Changes to These Terms of Service */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        13. Changes to These Terms of Service
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        We may modify these Terms of Service from time to time in which case we will update the "Last Updated" date at the top of these Terms. If we make changes that are material, we will use reasonable efforts to attempt to notify you, such as by e-mail and/or by placing a prominent notice on the first page of the Website. However, it is your sole responsibility to review these Terms from time to time to view any such changes. The updated Terms will be effective as of the time of posting, or such later date as may be specified in the updated Terms. Your continued access or use of the Services after the modifications have become effective will be deemed your acceptance of the modified Terms.
                    </Typography>
                </Paper>

                {/* Section 14: Force Majeure */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        14. Force Majeure
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Veridical shall not be liable for any delay, failure, or interruption in performance under these Terms (other than payment obligations) resulting from causes beyond its reasonable control, including but not limited to acts of God, natural disasters, fires, floods, earthquakes, epidemics, pandemics, acts of terrorism, war, civil unrest, labor disputes, governmental actions, failures of utilities or telecommunications networks, internet service provider failures, cloud service provider outages, or failures of third-party systems or services upon which the Service relies.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Veridical will use commercially reasonable efforts to resume performance as soon as practicable following any such event.
                    </Typography>
                </Paper>

                {/* Section 15: Contact Information */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        15. Contact Information
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        For questions regarding these Terms, the Privacy Policy please contact:
                    </Typography>
                    <Box sx={{ marginTop: '16px' }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Veridical Technologies LLC
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '8px'
                            }}
                        >
                            Email:{' '}
                            <Link
                                href="mailto:support@attorneyaide.com"
                                sx={{
                                    textDecoration: 'none',
                                    color: 'var(--text-primary)',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                support@attorneyaide.com
                            </Link>
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            Seattle, Washington
                        </Typography>
                    </Box>
                </Paper>
            </Container>
            <Footer />
        </div>
    );
}

